/*
 * SwipeScreen — TD "Swipe" advisor matching & booking flow.
 *
 * Ported from the FigmaMake export "SWIPE for IVA" and reskinned onto the app's shared
 * bh-* design system (styles/theme.css) and real chat vocabulary (ChatScreen's header/
 * bubble/input classes) so it looks and feels like the rest of the app instead of the
 * original hand-rolled Tailwind-utility shim it shipped with. The flow logic, step order,
 * and copy are unchanged — this is a visual + data-source pass only. Advisor data, question
 * options, and match scoring now live in ../constants/advisors.js, shared with the in-chat
 * matching flow (screens/swipe/chatSwipeFlow.js).
 */
import React, { useState, useEffect, useRef } from 'react';
import { MdArrowBack, MdChevronLeft, MdChat, MdSend, MdCheckCircle } from 'react-icons/md';
import {
  ADVISORS, classifyAdvisorType, deriveFollowUp, matchAdvisors,
  SNAPSHOT_OPTIONS, STYLE_OPTIONS, MEETING_OPTIONS, LANGUAGE_OPTIONS,
} from '../constants/advisors';
import { useProfile } from '../contexts/ProfileContext';
import { useApp } from '../contexts/AppContext';
import { saveSwipeBooking, getSwipePreferences } from '../services/backendAPI';

function isRecent(isoStr, days) {
  if (!isoStr) return false;
  return (Date.now() - new Date(isoStr).getTime()) < days * 24 * 60 * 60 * 1000;
}

const QUESTIONNAIRE_STEPS = [
  'confirm-details',
  'what-brings-you',
  'goal-followup',
  'financial-snapshot',
  'personality-style',
  'booking-preferences',
];

const STEP_LABELS = {
  'confirm-details': 'Your details',
  'what-brings-you': "Why you're here",
  'goal-followup': 'Your goals',
  'financial-snapshot': 'Financial snapshot',
  'personality-style': 'Advisor fit',
  'booking-preferences': 'Preferences',
};

const initials = (name = '') =>
  name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();

function chip(bg, color) {
  return {
    display: 'inline-flex', alignItems: 'center', background: bg, color,
    font: 'var(--bh-caption)', fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-full)',
  };
}

// ─── SHARED PRIMITIVES ────────────────────────────────────────────────────────

function FlowHeader({ title, onBack }) {
  return (
    <header className="bh-flow-hero">
      <div className="bh-flow-hero-left">
        {onBack && (
          <button className="bh-flow-back" onClick={onBack} aria-label="Back">
            <MdArrowBack />
          </button>
        )}
        <h1 className="bh-flow-title">{title}</h1>
      </div>
    </header>
  );
}

function StepProgress({ step }) {
  const idx = QUESTIONNAIRE_STEPS.indexOf(step);
  if (idx === -1) return null;
  const total = QUESTIONNAIRE_STEPS.length;
  const pct = Math.round(((idx + 1) / total) * 100);
  return (
    <div style={{ padding: '0 var(--space-md) var(--space-sm)', background: 'var(--td-green)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, font: 'var(--bh-caption)', fontWeight: 600, color: 'var(--bh-black)' }}>
        <span>Step {idx + 1} of {total}</span>
        <span>{STEP_LABELS[step]}</span>
      </div>
      <div style={{ height: 6, borderRadius: 'var(--radius-full)', background: 'rgba(0,0,0,0.15)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 'var(--radius-full)', background: 'var(--bh-black)', transition: 'width 300ms ease' }} />
      </div>
    </div>
  );
}

function OptionRow({ selected, onClick, icon, title, sub }) {
  return (
    <button type="button" className={`bh-account-row${selected ? ' selected' : ''}`} onClick={onClick} style={{ width: '100%', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {icon && <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>{icon}</span>}
        <div className="bh-account-row-info">
          <span className="bh-account-row-name">{title}</span>
          {sub && <span className="bh-account-row-num">{sub}</span>}
        </div>
      </div>
      {selected && <MdCheckCircle size={20} color="var(--td-green)" style={{ flexShrink: 0 }} />}
    </button>
  );
}

function NavButtons({ onBack, onNext, nextLabel = 'Next', nextDisabled = false, hideNext = false }) {
  return (
    <div style={{ marginTop: 'var(--space-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
      {!hideNext && (
        <button className="bh-btn-cta" onClick={onNext} disabled={nextDisabled}>{nextLabel}</button>
      )}
      {onBack && (
        <button className="bh-btn-outline-dark" onClick={onBack}>Back</button>
      )}
    </div>
  );
}

// ─── ENTRANCE SCREEN (fake chat preview, reskinned onto the real chat vocabulary) ─────

function EntranceScreen({ onStart, onBack }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [chipsDismissed, setChipsDismissed] = useState(false);
  const bottomRef = useRef(null);

  const BOT_SEQUENCE = [
    "Hi! I'm BankBot, your TD virtual assistant.",
    "I can help you book an appointment with a TD financial advisor who's matched to your goals.",
    'Would you like to get started?',
  ];

  useEffect(() => {
    let timer;
    let i = 0;
    const tick = () => {
      if (i < BOT_SEQUENCE.length) {
        i++;
        setMessages(BOT_SEQUENCE.slice(0, i).map((text) => ({ from: 'bot', text })));
        timer = setTimeout(tick, 900);
      }
    };
    timer = setTimeout(tick, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const showCta = () => {
    setChipsDismissed(true);
    setMessages((prev) => [
      ...prev,
      { from: 'bot', text: "I'll connect you with Swipe — TD's advisor matching tool. It only takes a few minutes to find your ideal advisor and book a time.", cta: true },
    ]);
  };

  const handleChip = (text) => {
    if (text === 'Book an appointment') {
      setMessages((prev) => [...prev, { from: 'user', text }]);
      setChipsDismissed(true);
      setTimeout(showCta, 600);
    } else {
      setChipsDismissed(true);
      setMessages((prev) => [
        ...prev,
        { from: 'user', text },
        { from: 'bot', text: 'I can help with that! For now, let me connect you with a TD advisor who can assist you in detail.' },
      ]);
    }
  };

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setInputValue('');
    setChipsDismissed(true);
    setMessages((prev) => [
      ...prev,
      { from: 'user', text: trimmed },
      { from: 'bot', text: 'I can help with that! Let me connect you with a TD advisor.', cta: true },
    ]);
  };

  return (
    <div className="screen chat-screen">
      <div className="simple-header chat-header-outer">
        <div className="chat-header-main-row">
          <MdChevronLeft className="back-icon" onClick={onBack} />
          <MdChat style={{ fontSize: 22, color: 'white' }} />
          <h1 className="header-title" style={{ marginLeft: 4 }}>Swipe</h1>
        </div>
      </div>

      <div className="scroll-content chat-content-bg">
        <div className="chat-timestamp">{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>

        {messages.map((msg, i) => (
          <div key={i} className={`chat-message-container ${msg.from === 'bot' ? 'bot-message' : 'user-message'}`}>
            {msg.from === 'bot' && <div className="bot-avatar"><MdChat /></div>}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: msg.from === 'bot' ? 'flex-start' : 'flex-end', maxWidth: msg.from === 'bot' ? '92%' : '78%', flex: msg.from === 'bot' ? 1 : undefined, minWidth: 0 }}>
              <div className={`chat-bubble${msg.from === 'bot' ? ' chat-bubble--flat' : ''}`}>{msg.text}</div>
              {msg.cta && (
                <div className="bh-form-card" style={{ marginTop: 8, padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: 'var(--space-sm) var(--space-md)', background: 'var(--td-dark-green)' }}>
                    <p style={{ margin: 0, color: 'white', fontWeight: 700, font: 'var(--bh-body)' }}>Swipe by TD</p>
                    <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.75)', font: 'var(--bh-caption)' }}>Advisor matching & booking</p>
                  </div>
                  <div style={{ padding: 'var(--space-sm) var(--space-md)' }}>
                    <p style={{ margin: '0 0 var(--space-sm)', color: 'var(--bh-gray)', font: 'var(--bh-caption)' }}>
                      Get matched with a financial advisor based on your goals, then book an appointment in minutes.
                    </p>
                    <button className="bh-btn-cta" onClick={onStart}>Open Swipe →</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="chat-input-area">
        {!chipsDismissed && messages.length >= BOT_SEQUENCE.length && (
          <div className="suggestions-row">
            {['Book an appointment', 'Learn about advisors', 'What do I need?'].map((c) => (
              <button key={c} className="chat-suggestion-chip" onClick={() => handleChip(c)}>{c}</button>
            ))}
          </div>
        )}
        <div className="input-row">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask me something"
          />
          <button className="send-button" onClick={handleSend}><MdSend /></button>
        </div>
      </div>
    </div>
  );
}

// ─── SELF-SERVE CHECK ─────────────────────────────────────────────────────────

function SelfServeCheckScreen({ onProceed, onSelfServe, onExit }) {
  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <div style={{ ...chip('var(--td-green-light)', 'var(--td-dark-green)'), marginBottom: 4 }}>Quick check</div>
          <h2 className="bh-form-card-title">
            Could you handle this <span style={{ color: 'var(--td-green)' }}>yourself</span> in 2 minutes?
          </h2>
          <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--bh-gray)', lineHeight: 1.5 }}>
            Many common tasks — like updating contact info, checking rates, or viewing account details — can be done right here in the app without booking an appointment.
          </p>
        </div>

        <button type="button" className="bh-form-card" onClick={onSelfServe} style={{ textAlign: 'left', border: '2px solid var(--td-green)', background: 'var(--td-green-light)', cursor: 'pointer' }}>
          <p style={{ margin: '0 0 2px', fontWeight: 700, font: 'var(--bh-body)', color: 'var(--bh-black)' }}>Yes — take me there ⚡</p>
          <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>I can probably sort this out in the app</p>
        </button>

        <button type="button" className="bh-form-card" onClick={onProceed} style={{ textAlign: 'left', cursor: 'pointer' }}>
          <p style={{ margin: '0 0 2px', fontWeight: 700, font: 'var(--bh-body)', color: 'var(--bh-black)' }}>No — I need to talk to someone</p>
          <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>This is something I want professional advice on</p>
        </button>
      </div>
    </div>
  );
}

// ─── CONFIRM DETAILS ─────────────────────────────────────────────────────────

function ConfirmDetailsScreen({ onNext, onBack, onExit }) {
  const { loadedProfileData } = useProfile() || {};
  const customer = loadedProfileData?.personal_info || {};
  const fullName = [customer.first_name, customer.last_name].filter(Boolean).join(' ') || 'You';
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    phone: customer.phone_number || '',
    email: customer.email || '',
  });

  if (editMode) {
    return (
      <div className="screen bh-flow">
        <FlowHeader title="Swipe" onBack={onExit} />
        <StepProgress step="confirm-details" />
        <div className="bh-flow-body">
          <div className="bh-form-card">
            <h2 className="bh-form-card-title">Update your details</h2>
            <p style={{ margin: '-4px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
              Make any changes and we will use these to book your appointment.
            </p>
            {['phone', 'email'].map((key) => (
              <div key={key} className="bh-field">
                <label htmlFor={`swipe-${key}`} className="bh-field-label" style={{ textTransform: 'capitalize' }}>{key}</label>
                <input
                  id={`swipe-${key}`}
                  type={key === 'email' ? 'email' : 'tel'}
                  className="bh-field-input"
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </div>
            ))}
            <NavButtons onBack={() => setEditMode(false)} onNext={() => { setEditMode(false); onNext({ name: fullName, phone: form.phone, email: form.email }); }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <StepProgress step="confirm-details" />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <h2 className="bh-form-card-title">
            Does this look <span style={{ color: 'var(--td-green)' }}>right</span>?
          </h2>
          <p style={{ margin: '-4px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
            {"We'll"} book under your TD profile. Just confirm and {"we'll"} keep going.
          </p>

          <div className="bh-review-rows">
            {[
              { label: 'Name', value: fullName },
              { label: 'Phone', value: form.phone },
              { label: 'Email', value: form.email },
              { label: 'Branch region', value: 'Downtown Toronto' },
            ].map(({ label, value }) => (
              <div key={label} className="bh-review-row">
                <span className="bh-review-row-label">{label}</span>
                <span className="bh-review-row-value">{value}</span>
              </div>
            ))}
          </div>

          <button className="bh-btn-cta" onClick={() => onNext({ name: fullName, phone: form.phone, email: form.email })}>Yes, {"that's"} me — continue</button>
          <button className="bh-btn-outline-dark" onClick={() => setEditMode(true)}>{"Something's"} off — let me fix it</button>
        </div>
      </div>
    </div>
  );
}

// ─── WHAT BRINGS YOU IN ───────────────────────────────────────────────────────

function WhatBringsYouScreen({ onNext, onBack, onExit }) {
  const [text, setText] = useState('');
  const [selected, setSelected] = useState(null);

  const PROMPTS = [
    'Something big is coming up and I want a plan',
    'I want my money working harder for me',
    'I want to get a handle on debt or spending',
    'I just want a second opinion on my finances',
  ];

  const handleChip = (prompt) => { setSelected(prompt); setText(''); };
  const handleSubmit = () => { const intent = text.trim() || selected || ''; if (intent) onNext(intent); };
  const canProceed = text.trim().length > 0 || selected !== null;

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <StepProgress step="what-brings-you" />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <h2 className="bh-form-card-title">
            What brings you <span style={{ color: 'var(--td-green)' }}>in today</span>?
          </h2>
          <p style={{ margin: '-4px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
            Type anything, or pick one that fits — your own words help us find the right advisor.
          </p>

          <div className="bh-field" style={{ position: 'relative' }}>
            <textarea
              className="bh-field-textarea"
              value={text}
              onChange={(e) => { setText(e.target.value); setSelected(null); }}
              placeholder="e.g. thinking about refinancing before rates change..."
              rows={3}
            />
            {text.trim() && (
              <button className="bh-btn-cta" style={{ marginTop: 'var(--space-2xs)' }} onClick={handleSubmit}>Continue →</button>
            )}
          </div>

          <p style={{ margin: 0, font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>or pick one that fits</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
            {PROMPTS.map((p) => (
              <OptionRow key={p} selected={selected === p} onClick={() => handleChip(p)} title={p} />
            ))}
          </div>

          {selected && <NavButtons onBack={onBack} onNext={handleSubmit} nextDisabled={!canProceed} />}
          {!selected && !text.trim() && <NavButtons onBack={onBack} hideNext />}
        </div>
      </div>
    </div>
  );
}

// ─── GOAL FOLLOW-UP ───────────────────────────────────────────────────────────

function GoalFollowupScreen({ intent, onNext, onBack, onExit }) {
  const followUp = deriveFollowUp(intent);
  const [selected, setSelected] = useState(null);

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <StepProgress step="goal-followup" />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <div className="bh-info-box">
            <em>&ldquo;{intent}&rdquo;</em>
          </div>

          <h2 className="bh-form-card-title">
            One <span style={{ color: 'var(--td-green)' }}>quick question</span>
          </h2>
          <p style={{ margin: '-4px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>{followUp.question}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
            {followUp.options.map((opt) => (
              <OptionRow key={opt} selected={selected === opt} onClick={() => setSelected(opt)} title={opt} />
            ))}
          </div>

          <NavButtons onBack={onBack} onNext={() => onNext(selected)} nextDisabled={!selected} />
        </div>
      </div>
    </div>
  );
}

// ─── FINANCIAL SNAPSHOT SCREEN ────────────────────────────────────────────────

function FinancialSnapshotScreen({ onNext, onBack, onExit }) {
  const [selected, setSelected] = useState(null);

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <StepProgress step="financial-snapshot" />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <h2 className="bh-form-card-title">
            One more thing — <span style={{ color: 'var(--td-green)' }}>totally optional</span>
          </h2>
          <p style={{ margin: '-4px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
            So we match you with an advisor who's the right fit for where you're at — roughly where do your savings and investments sit today? Totally fine to skip this.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
            {SNAPSHOT_OPTIONS.map(({ id, label, sub }) => (
              <OptionRow key={id} selected={selected === id} onClick={() => setSelected(id)} title={label} sub={sub} />
            ))}
          </div>

          <NavButtons onBack={onBack} onNext={() => onNext(selected)} nextDisabled={!selected} />
        </div>
      </div>
    </div>
  );
}

// ─── PERSONALITY STYLE SCREEN ─────────────────────────────────────────────────

const PERSONALITY_KEY = 'swipe_advisor_style';

function PersonalityStyleScreen({ onNext, onBack, onExit, onStyleCapture }) {
  const [selected, setSelected] = useState(null);
  const [saved, setSaved] = useState(false);

  const handleNext = () => {
    if (!selected) return;
    localStorage.setItem(PERSONALITY_KEY, selected);
    onStyleCapture(selected);
    setSaved(true);
    setTimeout(onNext, 900);
  };

  if (saved) {
    return (
      <div className="screen bh-flow">
        <div className="bh-flow-body" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <div className="bh-success-icon"><MdCheckCircle size={28} /></div>
          <p className="bh-success-title" style={{ marginTop: 'var(--space-sm)' }}>Got it — noted for next time</p>
          <p className="bh-success-subtitle">{"We'll"} remember this so you never have to answer it again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <StepProgress step="personality-style" />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <div style={{ ...chip('#fff7ed', '#9a3412') }}>✨ Asked once — remembered forever</div>
          <h2 className="bh-form-card-title">
            When you talk to an advisor, <span style={{ color: 'var(--td-green)' }}>what feels best</span> to you?
          </h2>
          <p style={{ margin: '-4px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
            This shapes how we match you — and {"we'll"} never ask again once you answer.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
            {STYLE_OPTIONS.map(({ id, label, icon }) => (
              <OptionRow key={id} selected={selected === label} onClick={() => setSelected(label)} icon={icon} title={label} />
            ))}
          </div>

          <NavButtons onBack={onBack} onNext={handleNext} nextDisabled={!selected} />
        </div>
      </div>
    </div>
  );
}

// ─── BOOKING PREFERENCES SCREEN ───────────────────────────────────────────────

const MEETING_KEY = 'swipe_meeting_method';
const LANGUAGE_KEY = 'swipe_language';

function BookingPreferencesScreen({ onNext, onBack, onExit }) {
  const [meetingType, setMeetingType] = useState(() => localStorage.getItem(MEETING_KEY));
  const [language, setLanguage] = useState(() => localStorage.getItem(LANGUAGE_KEY));
  const isReturning = !!(localStorage.getItem(MEETING_KEY) && localStorage.getItem(LANGUAGE_KEY));

  const handleNext = () => {
    if (!meetingType || !language) return;
    localStorage.setItem(MEETING_KEY, meetingType);
    localStorage.setItem(LANGUAGE_KEY, language);
    onNext({ meetingType, language });
  };

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <StepProgress step="booking-preferences" />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <h2 className="bh-form-card-title">
            {'Almost done — '}<span style={{ color: 'var(--td-green)' }}>your preferences</span>
          </h2>
          <p style={{ margin: '-4px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
            {isReturning
              ? 'We remembered your preferences from last time. Change anything below.'
              : "These get saved to your profile so you won't need to set them again."}
          </p>

          {isReturning && (
            <div style={{ ...chip('var(--td-green-light)', 'var(--td-dark-green)') }}>Remembered from your last booking</div>
          )}

          <p style={{ margin: 0, font: 'var(--bh-body-sm)', fontWeight: 700, color: 'var(--bh-black)' }}>How do you want to meet?</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)' }}>
            {MEETING_OPTIONS.map(({ id, label, icon, sub }) => (
              <OptionRow key={id} selected={meetingType === label} onClick={() => setMeetingType(label)} icon={icon} title={label} sub={sub} />
            ))}
          </div>

          <p style={{ margin: 0, font: 'var(--bh-body-sm)', fontWeight: 700, color: 'var(--bh-black)' }}>Preferred language</p>
          <div className="bh-quick-amounts">
            {LANGUAGE_OPTIONS.map((l) => (
              <button key={l} type="button" className={`bh-quick-amount${language === l ? ' selected' : ''}`} onClick={() => setLanguage(l)}>
                {l}
              </button>
            ))}
          </div>

          <NavButtons onBack={onBack} onNext={handleNext} nextDisabled={!meetingType || !language} />
        </div>
      </div>
    </div>
  );
}

// ─── ADVISOR MATCHING SCREEN ──────────────────────────────────────────────────

function AdvisorMatchingScreen({ onSelect, onBack, onExit, intent, advisorStyle, snapshotBracket }) {
  const { category } = deriveFollowUp(intent || '');
  const styleEntry = STYLE_OPTIONS.find((s) => s.label === advisorStyle) || null;
  const advisorType = classifyAdvisorType(intent || '');
  const ranked = matchAdvisors(ADVISORS, { category, style: styleEntry, snapshotBracket, advisorType });

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <div className="bh-flow-body">
        <p style={{ margin: '0 0 -8px', font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--td-dark-green)' }}>Matched for you</p>
        <h2 style={{ margin: 0, font: 'var(--bh-h2)', color: 'var(--bh-black)' }}>We found your matches</h2>
        <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
          Ranked by how well each advisor fits your goals, finances, and communication style.
        </p>

        {ranked.map((advisor) => (
          <div key={advisor.id} className="bh-product-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
              {advisor.photo
                ? <img src={advisor.photo} alt={advisor.name} className="bh-avatar size-lg" style={{ objectFit: 'cover' }} />
                : <div className="bh-avatar size-lg">{initials(advisor.name)}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <h3 className="bh-product-card-title">{advisor.name}</h3>
                    <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{advisor.title}</p>
                  </div>
                  <span style={{ ...chip('var(--td-green)', 'var(--bh-black)'), flexShrink: 0 }}>{advisor.matchScore}%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <span style={{ color: '#f5b700', font: 'var(--bh-caption)' }}>★★★★★</span>
                  <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{advisor.rating} ({advisor.reviews})</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {advisor.certs.map((c) => <span key={c} style={chip('var(--bh-light-gray)', 'var(--bh-black)')}>{c}</span>)}
                  {advisor.languages.map((l) => <span key={l} style={chip('var(--bh-light-gray)', 'var(--bh-gray)')}>{l}</span>)}
                </div>
              </div>
            </div>

            <div className="bh-info-box">
              <strong style={{ color: 'var(--bh-black)' }}>Why this match: </strong>{advisor.whyMatch}
            </div>

            <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{advisor.branch} — {advisor.address}</p>

            <button className="bh-btn-cta" onClick={() => onSelect(advisor)}>Book with {advisor.name.split(' ')[0]}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── APPOINTMENT DETAILS SCREEN ───────────────────────────────────────────────

const TIME_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '1:00 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
];

function AppointmentDetailsScreen({ advisor, onNext, onBack, onExit }) {
  const today = new Date();
  const dates = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i + 1);
    return d;
  }).filter((d) => d.getDay() !== 0 && d.getDay() !== 6);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const fmt = (d) => d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          {advisor.photo
            ? <img src={advisor.photo} alt={advisor.name} className="bh-avatar size-md" style={{ objectFit: 'cover' }} />
            : <div className="bh-avatar size-md">{initials(advisor.name)}</div>}
          <div>
            <p style={{ margin: 0, fontWeight: 700, font: 'var(--bh-body)', color: 'var(--bh-black)' }}>{advisor.name}</p>
            <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{advisor.branch}</p>
          </div>
        </div>

        <div>
          <h2 style={{ margin: '0 0 var(--space-2xs)', font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>Select a date</h2>
          <div className="bh-action-carousel">
            {dates.slice(0, 10).map((d) => {
              const s = fmt(d);
              const on = selectedDate === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSelectedDate(s)}
                  style={{
                    flex: '0 0 68px', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '10px 6px', borderRadius: 'var(--radius-md)', border: `2px solid ${on ? 'var(--td-green)' : '#E5E7EB'}`,
                    background: on ? 'var(--td-green)' : 'var(--bh-white)', color: on ? 'var(--bh-black)' : 'var(--bh-black)',
                  }}
                >
                  <span style={{ font: 'var(--bh-caption)', fontWeight: 600 }}>{d.toLocaleDateString('en-CA', { weekday: 'short' })}</span>
                  <span style={{ font: 'var(--bh-h3)', fontWeight: 700, lineHeight: 1.2 }}>{d.getDate()}</span>
                  <span style={{ font: 'var(--bh-caption)' }}>{d.toLocaleDateString('en-CA', { month: 'short' })}</span>
                </button>
              );
            })}
          </div>
        </div>

        {selectedDate && (
          <div>
            <h2 style={{ margin: '0 0 var(--space-2xs)', font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>Select a time</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 'var(--space-2xs)' }}>
              {TIME_SLOTS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`bh-quick-amount${selectedTime === t ? ' selected' : ''}`}
                  onClick={() => setSelectedTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        <NavButtons
          onBack={onBack}
          onNext={() => selectedDate && selectedTime && onNext(selectedDate, selectedTime)}
          nextDisabled={!selectedDate || !selectedTime}
        />
      </div>
    </div>
  );
}

// ─── REVIEW SCREEN ────────────────────────────────────────────────────────────

function ReviewScreen({ advisor, date, time, customerDetails, intent, goalFollowupAnswer, meetingType, language, referenceNumber, onConfirm, onBack, onExit }) {
  const contactLines = [
    customerDetails?.name,
    customerDetails?.email,
    customerDetails?.phone,
  ].filter(Boolean).join('\n');
  const reasonLines = [intent, goalFollowupAnswer].filter(Boolean).join('\n');
  const prefLines = [meetingType, language].filter(Boolean).join('\n');
  const items = [
    { title: 'Your contact details', value: contactLines || '—' },
    { title: 'Reason for appointment', value: reasonLines || '—' },
    { title: 'Booking preferences', value: prefLines || '—' },
    { title: 'Branch selection', value: `${advisor.address}` },
    { title: 'Date & time', value: `${date}\n${time} EST` },
  ];

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onExit} />
      <div className="bh-flow-body">
        <h2 style={{ margin: 0, font: 'var(--bh-h2)', color: 'var(--bh-black)' }}>Review your appointment</h2>
        <p style={{ margin: '-8px 0 0', font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>Please confirm the details below before booking.</p>

        <div className="bh-form-card" style={{ border: '1px solid var(--td-green)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {advisor.photo
              ? <img src={advisor.photo} alt={advisor.name} className="bh-avatar size-md" style={{ objectFit: 'cover' }} />
              : <div className="bh-avatar size-md">{initials(advisor.name)}</div>}
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: 'var(--bh-black)' }}>You will meet with {advisor.name}</p>
              <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{advisor.branch}</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, font: 'var(--bh-body-sm)', color: 'var(--bh-black)' }}>
            <div>📅 {date}</div>
            <div>🕐 {time} (1 hour)</div>
            {referenceNumber && <div>🔖 Reference #{referenceNumber}</div>}
          </div>
          <div className="bh-btn-row">
            <button className="bh-btn-outline-dark">Add to Calendar</button>
            <button className="bh-btn-outline-dark">Edit</button>
          </div>
        </div>

        <div className="bh-review-rows">
          {items.map((item) => (
            <div key={item.title} className="bh-review-row">
              <span className="bh-review-row-label">{item.title}</span>
              <span className="bh-review-row-value" style={{ whiteSpace: 'pre-wrap' }}>{item.value}</span>
            </div>
          ))}
        </div>

        <button className="bh-btn-cta" onClick={onConfirm}>Confirm appointment</button>
        <button className="bh-btn-outline-dark" onClick={onBack} style={{ color: 'var(--td-red)', borderColor: 'var(--td-red)' }}>Cancel Appointment</button>
      </div>
    </div>
  );
}

// ─── CONFIRMATION SCREEN ──────────────────────────────────────────────────────

function ConfirmationScreen({ advisor, date, time, advisorNotified, onDone,
  referenceNumber, intent, goalAnswer, meetingType, language, customerName, customerEmail }) {

  const handleDownload = () => {
    const lines = [
      'TD BANK — APPOINTMENT CONFIRMATION',
      '═══════════════════════════════════════',
      '',
      `Reference:     ${referenceNumber || ''}`,
      `Date:          ${date || ''}`,
      `Time:          ${time || ''} EST (1 hour)`,
      '',
      'ADVISOR',
      `Name:          ${advisor?.name || ''}`,
      `Title:         ${advisor?.title || ''}`,
      `Branch:        ${advisor?.branch || ''}`,
      `Address:       ${advisor?.address || ''}`,
      '',
      'APPOINTMENT DETAILS',
      `Topic:         ${intent || ''}`,
      `Specifics:     ${goalAnswer || ''}`,
      `Meeting type:  ${meetingType || ''}`,
      `Language:      ${language || ''}`,
      '',
      'YOUR CONTACT DETAILS',
      `Name:          ${customerName || ''}`,
      `Email:         ${customerEmail || ''}`,
      '',
      '───────────────────────────────────────',
      'Questions? Call TD Anytime: 1-866-222-3456',
      `Generated: ${new Date().toLocaleString('en-CA', { timeZone: 'America/Toronto' })}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TD-Appointment-${referenceNumber || 'confirmation'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" />
      <div className="bh-flow-body" style={{ alignItems: 'center', textAlign: 'center' }}>
        <div className="bh-success-icon"><MdCheckCircle size={40} /></div>
        <h2 className="bh-success-title">Appointment Confirmed!</h2>
        <p className="bh-success-subtitle">A confirmation has been sent to your email.</p>
        {advisorNotified !== false && (
          <p className="bh-success-subtitle" style={{ marginTop: 4 }}>Your advisor has been notified and will reach out to confirm.</p>
        )}

        <div className="bh-form-card" style={{ width: '100%', textAlign: 'left', border: '1px solid var(--td-green)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {advisor.photo
              ? <img src={advisor.photo} alt={advisor.name} className="bh-avatar size-md" style={{ objectFit: 'cover' }} />
              : <div className="bh-avatar size-md">{initials(advisor.name)}</div>}
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: 'var(--bh-black)' }}>{advisor.name}</p>
              <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{advisor.title}</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, font: 'var(--bh-body-sm)', color: 'var(--bh-black)' }}>
            <p style={{ margin: 0 }}>📅 {date}</p>
            <p style={{ margin: 0 }}>🕐 {time} EST (1 hour)</p>
            <p style={{ margin: 0 }}>📍 {advisor.address}</p>
            <p style={{ margin: 0 }}>🔖 Reference #{referenceNumber || ''}</p>
          </div>
        </div>

        <button className="bh-btn-outline-dark" onClick={handleDownload}>⬇ Download confirmation</button>
        <button className="bh-btn-cta" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

// ─── SELF-SERVE INTENT ────────────────────────────────────────────────────────

function resolveSelfServeScreen(text) {
  const t = text.toLowerCase();
  if (/rate|mortgage rate|interest/.test(t))           return 'todaysRates';
  if (/balance|account|statement|history/.test(t))     return 'accounts';
  if (/card|lock|freeze|cancel card/.test(t))          return 'cardMgmt';
  if (/document/.test(t))                              return 'statements';
  if (/bill|pay|payee/.test(t))                        return 'billPayment';
  if (/transfer|etransfer|send money|interac/.test(t)) return 'emailTransfer';
  if (/contact|address|phone|email|profile/.test(t))   return 'profileScreen';
  if (/spend|budget|category/.test(t))                 return 'spendingAnalysis';
  if (/goal|saving/.test(t))                           return 'goalsScreen';
  if (/invest|portfolio|stock/.test(t))                return 'portfolio';
  return 'financialSummary';
}

function SelfServeIntentScreen({ onSubmit, onBack, onExit }) {
  const [text, setText] = React.useState('');
  return (
    <div className="screen bh-flow">
      <FlowHeader title="Swipe" onBack={onBack} />
      <div className="bh-flow-body">
        <div className="bh-form-card">
          <div style={{ ...chip('var(--td-green-light)', 'var(--td-dark-green)'), marginBottom: 4 }}>Self-serve</div>
          <h2 className="bh-form-card-title">What were you hoping to do?</h2>
          <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--bh-gray)', lineHeight: 1.5 }}>
            Describe it briefly and we'll take you straight there.
          </p>
        </div>
        <div className="bh-form-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. check my account balance, lock my card, pay a bill…"
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'none', borderRadius: 8, border: '1.5px solid var(--bh-border)', padding: '10px 12px', font: 'var(--bh-body-sm)', outline: 'none' }}
          />
          <button
            type="button"
            className="send-button"
            disabled={!text.trim()}
            onClick={() => onSubmit(text.trim())}
            style={{ alignSelf: 'flex-end', padding: '10px 22px', borderRadius: 8 }}
          >
            Take me there ⚡
          </button>
        </div>
        <button type="button" className="bh-form-card" onClick={onBack} style={{ textAlign: 'left', cursor: 'pointer' }}>
          <p style={{ margin: '0 0 2px', fontWeight: 700, font: 'var(--bh-body)', color: 'var(--bh-black)' }}>Actually, I need to talk to someone</p>
          <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>Book an appointment instead</p>
        </button>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────

const SwipeScreen = ({ onBack, onNavigate }) => {
  const { sessionSummary } = useApp() || {};
  const [step, setStep] = useState('entrance');
  const [intent, setIntent] = useState('');
  const [goalFollowupAnswer, setGoalFollowupAnswer] = useState('');
  const [snapshotBracket, setSnapshotBracket] = useState('');
  const [meetingType, setMeetingType] = useState(() => localStorage.getItem(MEETING_KEY) ?? '');
  const [language, setLanguage] = useState(() => localStorage.getItem(LANGUAGE_KEY) ?? '');
  const [customerDetails, setCustomerDetails] = useState({});
  const [advisorStyle, setAdvisorStyle] = useState(() => localStorage.getItem(PERSONALITY_KEY) ?? '');
  const [selectedAdvisor, setSelectedAdvisor] = useState(ADVISORS[0]);
  const [apptDate, setApptDate] = useState('');
  const [apptTime, setApptTime] = useState('');
  const [swipePrefs, setSwipePrefs] = useState(null);
  const [advisorNotified, setAdvisorNotified] = useState(true);
  const [referenceNumber, setReferenceNumber] = useState('');

  useEffect(() => {
    const customerId = sessionSummary?.customerId;
    if (!customerId) return;
    getSwipePreferences(customerId).then(prefs => { if (prefs) setSwipePrefs(prefs); }).catch(() => {});
  }, [sessionSummary?.customerId]);

  const profileSkip = isRecent(swipePrefs?.profileLastUpdated, 7);
  const incomeSkip = isRecent(swipePrefs?.incomeLastRefreshed, 30);

  const go = (s) => setStep(s);
  const next = (s) => () => go(s);
  const exit = next('entrance');

  const persistBooking = async () => {
    const customerId = sessionSummary?.customerId;
    if (!customerId) return;
    const result = await saveSwipeBooking(customerId, {
      advisor: selectedAdvisor,
      intent,
      goalFollowupAnswer,
      meetingType,
      language,
      date: apptDate,
      time: apptTime,
      customerDetails,
      snapshotBracket,
      advisorType: classifyAdvisorType(intent),
    });
    setAdvisorNotified(result?.advisorNotified ?? true);
    if (result?.referenceNumber) setReferenceNumber(result.referenceNumber);
  };

  const hasPersonality = !!localStorage.getItem(PERSONALITY_KEY);

  switch (step) {
    case 'entrance':
      return <EntranceScreen onStart={next('self-serve-check')} onBack={onBack} />;
    case 'self-serve-check':
      return <SelfServeCheckScreen onProceed={profileSkip ? next('what-brings-you') : next('confirm-details')} onSelfServe={next('self-serve-intent')} onExit={exit} />;
    case 'self-serve-intent':
      return <SelfServeIntentScreen
        onSubmit={(text) => { if (onNavigate) onNavigate(resolveSelfServeScreen(text)); else go('entrance'); }}
        onBack={next('self-serve-check')}
        onExit={exit}
      />;
    case 'confirm-details':
      return <ConfirmDetailsScreen onNext={(details) => { setCustomerDetails(details); go('what-brings-you'); }} onBack={next('self-serve-check')} onExit={exit} />;
    case 'what-brings-you':
      return (
        <WhatBringsYouScreen
          onNext={(i) => { setIntent(i); go('goal-followup'); }}
          onBack={next('confirm-details')}
          onExit={exit}
        />
      );
    case 'goal-followup':
      return <GoalFollowupScreen intent={intent} onNext={(ans) => {
        setGoalFollowupAnswer(ans);
        if (incomeSkip) {
          setSnapshotBracket(swipePrefs?.snapshotBracket || '');
          go(hasPersonality ? 'booking-preferences' : 'personality-style');
        } else {
          go('financial-snapshot');
        }
      }} onBack={next('what-brings-you')} onExit={exit} />;
    case 'financial-snapshot':
      return (
        <FinancialSnapshotScreen
          onNext={(bracket) => { setSnapshotBracket(bracket); go(hasPersonality ? 'booking-preferences' : 'personality-style'); }}
          onBack={next('goal-followup')}
          onExit={exit}
        />
      );
    case 'personality-style':
      return (
        <PersonalityStyleScreen
          onNext={next('booking-preferences')}
          onBack={next(incomeSkip ? 'goal-followup' : 'financial-snapshot')}
          onExit={exit}
          onStyleCapture={(s) => setAdvisorStyle(s)}
        />
      );
    case 'booking-preferences':
      return (
        <BookingPreferencesScreen
          onNext={({ meetingType: mt, language: lang }) => { setMeetingType(mt); setLanguage(lang); go('advisor-matching'); }}
          onBack={next(hasPersonality ? (incomeSkip ? 'goal-followup' : 'financial-snapshot') : 'personality-style')}
          onExit={exit}
        />
      );
    case 'advisor-matching':
      return (
        <AdvisorMatchingScreen
          intent={intent}
          advisorStyle={advisorStyle}
          snapshotBracket={snapshotBracket}
          onSelect={(advisor) => { setSelectedAdvisor(advisor); go('appointment-details'); }}
          onBack={next('booking-preferences')}
          onExit={exit}
        />
      );
    case 'appointment-details':
      return (
        <AppointmentDetailsScreen
          advisor={selectedAdvisor}
          onNext={(d, t) => { setApptDate(d); setApptTime(t); go('review'); }}
          onBack={next('advisor-matching')}
          onExit={exit}
        />
      );
    case 'review':
      return (
        <ReviewScreen
          advisor={selectedAdvisor}
          date={apptDate}
          time={apptTime}
          customerDetails={customerDetails}
          intent={intent}
          goalFollowupAnswer={goalFollowupAnswer}
          meetingType={meetingType}
          language={language}
          referenceNumber={referenceNumber}
          onConfirm={() => { persistBooking().then(() => go('confirmation')); }}
          onBack={next('appointment-details')}
          onExit={exit}
        />
      );
    case 'confirmation':
      return (
        <ConfirmationScreen
          advisor={selectedAdvisor}
          date={apptDate}
          time={apptTime}
          advisorNotified={advisorNotified}
          referenceNumber={referenceNumber}
          intent={intent}
          goalAnswer={goalFollowupAnswer}
          meetingType={meetingType}
          language={language}
          customerName={customerDetails?.name || ''}
          customerEmail={customerDetails?.email || ''}
          onDone={next('entrance')}
        />
      );
    default:
      return null;
  }
};

export default SwipeScreen;
